import {gql, useQuery} from '@apollo/client';
import styles from './exchanges.module.scss';

/**
 * Exchange trading state, for reading an order queue.
 *
 * An ops person judging whether a queue is stuck needs to know whether the
 * market is even open and when the cutoff was — that state exists on the
 * exchange record and was invisible on the Orders screen.
 */
const GET_EXCHANGE_TRADING_STATE = gql`
  query ExchangeTradingState($input: GetExchangesInput) {
    getExchanges(input: $input) {
      ... on Error {
        message
      }
      ... on ResponseWithExchange {
        data {
          exchange {
            id
            code
            stockExchangeName
            orderProcessingCutoffTime
            estimatedSettlementDays
            estimatedProcessingDays
            enableLimitOrder
            enableMarketOrder
            tradeable
            closure {
              title
              message
              isMarketOpen
            }
          }
        }
      }
    }
  }
`;

/**
 * Cutoff times come back unpadded — BRVM stores "13:9". Pad for display only;
 * the stored value is never rewritten from here.
 */
const formatCutoff = value => {
  if (!value) return null;
  const [hour, minute] = String(value).split(':');
  if (minute === undefined) return value;
  return `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
};

const Stat = ({label, value}) => (
  <div className={styles.stat}>
    <div className={styles.statLabel}>{label}</div>
    <div className={styles.statValue}>{value ?? '—'}</div>
  </div>
);

export const OrderExchangeContext = ({exchangeId}) => {
  const {data} = useQuery(GET_EXCHANGE_TRADING_STATE, {
    variables: {
      input: {
        exchangeId: [exchangeId],
        includeSectors: false,
        includeCompositeIndex: false,
        moversLimit: 0,
      },
    },
    skip: !exchangeId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
  });

  if (!exchangeId) return null;

  const exchange = (data?.getExchanges?.data ?? []).find(
    node => node.exchange,
  )?.exchange;
  if (!exchange) return null;

  const closure = exchange.closure;
  // `isMarketOpen` is the only field here that changes minute to minute, and
  // it is what decides whether a stalled queue is a problem or just closed.
  const open = closure?.isMarketOpen;

  return (
    <div className={styles.contextStrip}>
      <div className={styles.contextHead}>
        <span className={styles.venueCode}>{exchange.code}</span>
        <span
          className={`${styles.flag} ${open ? styles.flagOn : styles.flagOff}`}>
          {open ? 'Market open' : 'Market closed'}
        </span>
        {!exchange.tradeable && (
          <span className={`${styles.flag} ${styles.flagOff}`}>
            Not tradeable
          </span>
        )}
      </div>

      <div className={styles.contextStats}>
        <Stat
          label="Cutoff"
          value={formatCutoff(exchange.orderProcessingCutoffTime)}
        />
        <Stat
          label="Settles in"
          value={
            exchange.estimatedSettlementDays != null
              ? `${exchange.estimatedSettlementDays}d`
              : null
          }
        />
        <Stat
          label="Processing"
          value={
            exchange.estimatedProcessingDays != null
              ? `${exchange.estimatedProcessingDays}d`
              : null
          }
        />
        <Stat
          label="Order types"
          value={
            [
              exchange.enableLimitOrder && 'Limit',
              exchange.enableMarketOrder && 'Market',
            ]
              .filter(Boolean)
              .join(' · ') || 'None'
          }
        />
      </div>

      {!open && closure?.message && (
        <div className={styles.contextNote}>
          <strong>{closure.title || 'Closed'}</strong> — {closure.message}
        </div>
      )}
    </div>
  );
};

export default OrderExchangeContext;
